import { useAuth } from "@/core/hooks/useAuth";
import { checkPasswordStrength } from "@/shared/utils/password";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import log from "loglevel";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export type ResetPasswordFormValues = {
  password: string;
  passwordConfirmation: string;
};

export const useResetPasswordController = () => {
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAuth();

  const resetCode = searchParams.get("code") ?? "";

  const [passwordReset, setPasswordReset] = useState(false);

  const [loading, { open: openLoading, close: closeLoading }] =
    useDisclosure(false);

  const form = useForm<ResetPasswordFormValues>({
    initialValues: {
      password: "",
      passwordConfirmation: "",
    },
    validateInputOnBlur: true,
    validate: {
      password: (value) => {
        const cleanedValue = value.trim();
        if (!cleanedValue) {
          return "Senha é obrigatória";
        }
        const { isValid } = checkPasswordStrength(cleanedValue);

        if (!isValid) {
          return "A senha não atende a todos os requisitos";
        }

        return null;
      },

      passwordConfirmation: (value, values) => {
        const cleanedValue = value.trim();

        if (!cleanedValue) {
          return "Confirmação de senha é obrigatória";
        }

        const cleanedPassword = values.password.trim();
        return cleanedValue === cleanedPassword
          ? null
          : "As senhas devem ser idênticas";
      },
    },
  });

  useEffect(() => {
    if (!resetCode) {
      notifications.show({
        title: "Link inválido",
        message:
          "O link de redefinição de senha é inválido ou está incompleto. Solicite um novo link.",
        color: "red",
      });
    }
  }, [resetCode]);

  const handleResetPassword = async () => {
    if (!resetCode) {
      notifications.show({
        title: "Não foi possível redefinir a senha",
        message:
          "O código de redefinição não foi encontrado. Tente solicitar um novo e-mail de redefinição.",
        color: "red",
      });
      return;
    }

    openLoading();
    try {
      const password = form.values.password.trim();
      const passwordConfirmation = form.values.passwordConfirmation.trim();

      await resetPassword(resetCode, password, passwordConfirmation);

      notifications.show({
        title: "Senha redefinida com sucesso!",
        message: "Você já pode acessar sua conta com a nova senha.",
        color: "teal",
        autoClose: 3000,
      });

      setPasswordReset(true);
    } catch (error: unknown) {
      log.error(error);

      const typedError = error as {
        response?: { data?: { error?: { message?: string } } };
      };

      const apiMessage =
        typedError.response?.data?.error?.message === "Incorrect code provided"
          ? "Este link de redefinição é inválido ou já foi utilizado."
          : "Ocorreu um erro ao redefinir sua senha. Tente novamente em instantes.";

      notifications.show({
        title: "Erro ao redefinir senha",
        message: apiMessage,
        color: "red",
        autoClose: 4000,
      });
    } finally {
      closeLoading();
      form.reset();
    }
  };

  return {
    form,
    loading,
    handleResetPassword,
    passwordReset,
  };
};
